import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { setToken, clearToken, getToken } from "@/lib/api";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadMe = useCallback(async () => {
    if (!getToken()) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
    } catch (e) {
      clearToken();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMe();
  }, [loadMe]);

  const devLogin = async (email, role, name) => {
    const res = await api.post("/auth/dev-login", { email, role, name });
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data.user;
  };

  const startGoogleLogin = async () => {
    const cfg = await api.get("/auth/config");
    const clientId = cfg.data.google_client_id;
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUri = window.location.origin + "/auth/google";
    const state = Math.random().toString(36).slice(2);
    sessionStorage.setItem("oauth_state", state);
    const params = new URLSearchParams({
      client_id: clientId,
      redirect_uri: redirectUri,
      response_type: "code",
      scope: "openid email profile",
      access_type: "offline",
      include_granted_scopes: "true",
      prompt: "select_account",
      state,
    });
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
  };

  const completeGoogleLogin = async (code) => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUri = window.location.origin + "/auth/google";
    const res = await api.post("/auth/google/callback", { code, redirect_uri: redirectUri });
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data.user;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      /* stateless, ignore */
    }
    clearToken();
    setUser(null);
  };

  const value = { user, loading, devLogin, startGoogleLogin, completeGoogleLogin, logout, refresh: loadMe };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
